/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useLazyGetPublicJobBySlugQuery } from 'services/jobsV2';
import {
  createWidgetConfig,
  createDynamicFindemIframe,
  cleanupFindemWidget,
  findemWidgetEventNames,
} from 'helpers/findemWidget';
import { useLocalStorage } from 'hooks/useLocalStorage';
import { useLazyGetNetworkQuery } from 'services/organizationsV2';
import { useUpdateSourcingCampaignMutation } from 'services/findemV2';
import { updateJobSourcingCampaign } from 'hooks/useJobs/actions';
import { useDispatch } from 'react-redux';

const getStorageKey = (collectionId, jobSlug) => `findem_calibration_session_${collectionId}_${jobSlug}`;

export const useSharedJobsCalibration = () => {
  const { collection_id: collectionId, job_slug: jobSlug } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [getNetwork, { isLoading: isLoadingNetwork, isFetching: isFetchingNetwork }] = useLazyGetNetworkQuery();
  const [getPublicJobBySlug, { data: job }] = useLazyGetPublicJobBySlugQuery();
  const [sessionId, setSessionId] = useLocalStorage(getStorageKey(collectionId, jobSlug), null);
  const [showHowItWorksModal, setShowHowItWorksModal] = useLocalStorage(
    `shared_jobs_calibration_modal_${collectionId}`,
    true,
  );
  const [updateSourcingCampaign] = useUpdateSourcingCampaignMutation();
  const dispatch = useDispatch();

  // Save session to localStorage
  const saveSessionToStorage = useCallback(
    (newSessionId) => {
      if (collectionId && jobSlug && newSessionId) {
        const storageKey = getStorageKey(collectionId, jobSlug);
        localStorage.setItem(storageKey, newSessionId);
        setSessionId(newSessionId);
      }
    },
    [collectionId, jobSlug, setSessionId],
  );

  // Listen to session change events
  useEffect(() => {
    const handleSessionChange = (event) => {
      const { payload } = event.detail;
      // Check if this session change is for our collection and job
      if (payload?.sessionId && String(payload?.collection_id) === String(collectionId)) {
        saveSessionToStorage(payload.sessionId);
      }
    };

    const handleCalibrationCompleted = (event) => {
      const { payload } = event.detail;
      if (String(payload?.collection_id) === String(collectionId) && payload?.view === 'calibration') {
        updateSourcingCampaign({
          id: payload.sourcing_campaign_id,
          status: 'ready_to_launch',
        });
        if (payload.job_id) {
          dispatch(
            updateJobSourcingCampaign({
              jobId: parseInt(payload.job_id, 10),
              sourcingCampaign: { status: 'ready_to_launch' },
            }),
          );
        }
      }
    };
    window.addEventListener(findemWidgetEventNames.FINDEM_WIDGET_CALIBRATION_COMPLETED, handleCalibrationCompleted);
    window.addEventListener(findemWidgetEventNames.FINDEM_WIDGET_SESSION_CHANGE, handleSessionChange);

    return () => {
      window.removeEventListener(findemWidgetEventNames.FINDEM_WIDGET_SESSION_CHANGE, handleSessionChange);
      window.removeEventListener(
        findemWidgetEventNames.FINDEM_WIDGET_CALIBRATION_COMPLETED,
        handleCalibrationCompleted,
      );
    };
  }, [collectionId, saveSessionToStorage]);

  // Initialize widget
  const initializeWidget = useCallback(
    async ({ jobData, token, vcInfo, vcUrl, jobBoardUrl }) => {
      setError(null);

      try {
        // Create widget configuration adapted for public use
        const config = createWidgetConfig({
          job: jobData,
          user: null, // No authenticated user for public page
          token,
          collectionId,
          view: 'calibration',
          vcInfo,
          vcUrl,
          jobBoardUrl,
        });

        // Override session metadata for public context
        config.sessionMetadata = {
          source: 'public_calibration',
          user_id: 'public_user',
          job_id: jobData?.id?.toString() || null,
          collection_id: collectionId || null,
          sourcing_campaign_id: jobData?.sourcingCampaign?.id || null,
        };

        // Use sessionId from localStorage if available
        if (sessionId) {
          config.sessionId = sessionId;
        }

        await createDynamicFindemIframe(config);
        setIsLoading(false);
      } catch (err) {
        setError(err);
        setIsLoading(false);
      }
    },
    [collectionId, sessionId],
  );

  // Fetch job and auth token, then initialize widget
  useEffect(() => {
    (async () => {
      if (!collectionId || !jobSlug) {
        setError(new Error('Missing collection ID or job slug'));
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        const urlParams = new URLSearchParams(window.location.search);
        const token = urlParams.get('calibrate-token');
        const sourcingCampaignId = urlParams.get('sourcing_campaign_id');

        if (!token) {
          throw new Error('Missing authentication token');
        }

        // Fetch job data
        const jobResponse = await getPublicJobBySlug({
          jobSlug,
          collectionId,
        }).unwrap();

        if (!jobResponse) {
          throw new Error('Job not found');
        }

        const network = await getNetwork(collectionId).unwrap();

        if (!network) {
          throw new Error('Network not found');
        }

        // Use sourcing campaign id from URL if available, otherwise fall back to job data
        const finalJobData = sourcingCampaignId
          ? {
              ...jobResponse,
              sourcingCampaign: {
                ...jobResponse?.sourcingCampaign,
                id: sourcingCampaignId,
              },
            }
          : jobResponse;

        // Initialize widget
        await initializeWidget({
          token,
          jobData: finalJobData,
          vcInfo: network?.description,
          vcUrl: network?.domain,
          jobBoardUrl: network?.url,
        });
      } catch (err) {
        setError(err);
        setIsLoading(false);
      }
    })();

    // Cleanup on unmount
    return () => {
      cleanupFindemWidget();
    };
  }, []);

  return {
    isLoading: isLoading || isFetchingNetwork || isLoadingNetwork,
    error,
    job,
    sessionId,
    setSessionId,
    showHowItWorksModal,
    setShowHowItWorksModal,
  };
};
